import { Tooltip, TooltipContent, TooltipTrigger } from "@aircall/ds";
import { type ReactNode, useMemo } from "react";

import { ScoreBadge } from "@/blocks/ScoreBadge";
import { formatScoreDisplay } from "@/helpers/number/number.helpers";
import { getColorFromRange } from "@/helpers/range/range.helpers";
import { ColorScale } from "@/helpers/range/range.helpers.decl";

type Rating = "excellent" | "good" | "fair" | "poor" | "very_poor" | "na";

type PercentBadgeProps = {
  // Null is "Not applicable"
  value: number | null;
  borderless?: boolean;
  // When set, the badge is wrapped in a hover tooltip showing this content.
  tooltip?: ReactNode;
};

const ratingTone: Record<Rating, ScoreBadge.Props["tone"]> = {
  excellent: "success",
  // `good` keeps the green (border-primary) border, distinct from `excellent`.
  good: "success-alt",
  fair: "warning",
  poor: "destructive",
  very_poor: "destructive",
  na: "muted",
};

const ratingScale: ColorScale<Exclude<Rating, "na">> = {
  excellent: [85, 100],
  good: [70, 85],
  fair: [50, 70],
  poor: [20, 50],
  very_poor: [0, 20],
};

export function PercentBadge({
  value,
  borderless = false,
  tooltip,
}: PercentBadgeProps) {
  const isNegative = (value ?? 0) < 0;
  const isGreaterThan100 = (value ?? 0) > 100;

  const rating: Rating = useMemo(() => {
    if (value === null) {
      return "na";
    }

    if (isNegative) {
      return "very_poor";
    }

    if (isGreaterThan100) {
      return "excellent";
    }

    return getColorFromRange(ratingScale)(value) ?? "na";
  }, [isNegative, isGreaterThan100, value]);

  const computedContent = useMemo(() => {
    if (value === null) {
      return "--";
    }

    if (isNegative) {
      return `${formatScoreDisplay(0)} %`;
    }

    // 100 and above is clamped and shown as a clean "100 %" (no trailing .0).
    if (value >= 100) {
      return "100 %";
    }

    return `${formatScoreDisplay(value)} %`;
  }, [isNegative, value]);

  const badgeProps = {
    tone: ratingTone[rating],
    borderless,
    "data-test": `percent-badge-${value}`,
  };

  if (tooltip == null) {
    return <ScoreBadge {...badgeProps}>{computedContent}</ScoreBadge>;
  }

  return (
    <Tooltip>
      {/* ScoreBadge is a forwardRef div that spreads props, so it can be the
          trigger directly (no wrapper node) via the DS `render` pattern. */}
      <TooltipTrigger
        delay={0}
        closeDelay={0}
        render={<ScoreBadge {...badgeProps} />}
      >
        {computedContent}
      </TooltipTrigger>
      <TooltipContent side="top" align="center">
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
}
